import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { connect } from 'react-redux';

import { siteboardAction } from '../actions/defaultActions';
import { 
  SET_EDIT_SUBSCRIPTION_MANAGERS_DIALOGUE_OPEN, ADD_SUBSCRIPTION_ADMIN, SET_ALERT, REMOVE_SUBSCRIPTION_ADMIN, REMOVE_PENDING_SUBSCRIPTION_ADMIN, 
} from '../actions/types';
import { objectKeysToList } from '../Util';

import PopupWindow from './PopupWindow';

// icon imports
import AddIcon from '../icons/addIcon';
import EmailIcon from '../icons/emailIcon';
import CloseIcon from '../icons/closeIcon';
import ClockIcon from '../icons/clockIcon';
import CircleCheckIcon from '../icons/circleCheckIcon';
import SendIcon from '../icons/sendIcon';
 
const EditSubscriptionManagersDialogue = ({ _users, _userId, _subscriptions, siteboardAction }) => {
  const inputEmailRef = useRef(null);

  const [inputEmail, setInputEmail] = useState('');
  const [inviteSentConfirmation, setInviteSentConfirmation] = useState(false);
  const [userAddedConfirmation, setUserAddedConfirmation] = useState(false);

  let ownedSubscription = null;
  try {
    if(_subscriptions[_users[_userId].ownedSubscriptionId].members[_userId].role === 'Owner') {
      ownedSubscription = _subscriptions[_users[_userId].ownedSubscriptionId];
    }
  } catch(error) {}

  if(ownedSubscription === null) return <></>;
  
  const subscriptionManagers = [];
  for(const memberId in ownedSubscription.members) {
    if(ownedSubscription.members[memberId].role === 'Admin') {
      subscriptionManagers.push({
        id: memberId, 
        ...ownedSubscription[memberId],
      });
    }
  }

  const pendingSubscriptionManagers = [];
  for(const email in ownedSubscription.pendingMembers) {
    if(ownedSubscription.pendingMembers[email].role === 'Admin') {
      pendingSubscriptionManagers.push({
        email: email, 
        ...ownedSubscription[email],
      });
    }
  }

  const projectManagerCount = subscriptionManagers.length + pendingSubscriptionManagers.length;

  const myEmail = _users[_userId].email;
  const suggestedEmailsMap = {};
  const managerEmailsMap = {};
  const pendingManagerEmailsMap = {};
  const subscriptionMemberEmailsMap = {};
  try {
    for(const userId in _subscriptions[ownedSubscription.id].members) {
      subscriptionMemberEmailsMap[_users[userId].email] = '';
    }
  } catch(error) {}
  for(const email in ownedSubscription.pendingMembers) {
    if(ownedSubscription.pendingMembers[email].role === 'Admin') {
      pendingManagerEmailsMap[email] = '';
    }
  }
  for(const userId in ownedSubscription.members) {
    if(!(userId in _users)) continue;
    if(ownedSubscription.members[userId].role === 'Admin') {
      managerEmailsMap[_users[userId].email] = '';
    }
  }
  for(const userId in _users) {
    if(_users[userId].email !== myEmail && !(_users[userId].email in managerEmailsMap) && !(_users[userId].email in pendingManagerEmailsMap)) {
      suggestedEmailsMap[_users[userId].email] = '';
    }
  }
  const suggestedEmails = objectKeysToList(suggestedEmailsMap);
  const inputEmailIsSubscriptionMember = inputEmail.trim() in subscriptionMemberEmailsMap;
  const inputEmailAlreadyAdded = inputEmail.trim() in managerEmailsMap;
  const inputEmailAlreadyInvited = inputEmail.trim() in pendingManagerEmailsMap;

  const closeDialogue = () => {
    siteboardAction(SET_EDIT_SUBSCRIPTION_MANAGERS_DIALOGUE_OPEN, { isOpen: false });
  };

  return (
    <PopupWindow title='Account Managers' closeDialogue={closeDialogue} bgStyle='bg-[#333]'>
      <div className='text-white max-w-screen-lg'>
        <div className='w-[90vw] max-w-full'></div>
        <div className='flex flex-col gap-y-8'>
          <div>
            <div>Assign managers for your account.</div>
            <div>Account Managers have the authority to create, edit, and assign members to all projects within your account.</div>
          </div>
          {/* Invite by email */}
          <div className='flex flex-col gap-y-4'>
            <div>
              <div className='flex flex-col sm:flex-row items-start gap-y-1 justify-between'>
                <div className={'flex justify-end font-bold text-[1.4em] text-[#f9a928]'}>Invite by email</div>
              </div>
              <div>
                <div>Send an email invitation to a colleague, granting them access to {process.env.REACT_APP_WEBSITE_NAME} as an account manager.</div>
                <div className='opacity-60'>If the email is already associated with this account, they will be automatically added without the need to accept an invitation.</div>
              </div>
            </div>
            <div className='w-full'>
              <label className='block text-sm font-medium mb-1'>Email</label>
              <input 
                ref={inputEmailRef}
                list='suggestedEmails'
                className='w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]' 
                type='text' 
                value={inputEmail}
                onChange={(event) => { 
                  setInputEmail(event.target.value);
                }}
              />
              <datalist id='suggestedEmails'>
                {suggestedEmails.map((email, index) => (<option value={email} key={index} />))}
              </datalist>
            </div>
            <button  
              className='relative flex justify-center items-center bg-[#471cff] hover:bg-[#603aff] py-2 font-bold cursor-pointer select-none rounded-lg' 
              disabled={inviteSentConfirmation || userAddedConfirmation || inputEmailAlreadyAdded || inputEmailAlreadyInvited}
              onClick={() => {
                try { 
                  if (typeof inputEmail !== 'string' || inputEmail.length === 0) throw new Error('Please enter the account manager\'s email');
                  siteboardAction(ADD_SUBSCRIPTION_ADMIN, {
                    subscriptionId: ownedSubscription.id, 
                    email: inputEmail, 
                  }, () => { 
                    if(inputEmailIsSubscriptionMember) {
                      setUserAddedConfirmation(true);
                      setTimeout(() => { setUserAddedConfirmation(false); }, 3000);
                    } else {
                      setInviteSentConfirmation(true);
                      setTimeout(() => { setInviteSentConfirmation(false); }, 3000);
                    }
                    setInputEmail('');
                  });
                } 
                catch(error) { 
                  siteboardAction(SET_ALERT, { message: error.message }); 
                }
              }}
            >
              <div className='flex items-center justify-center gap-2'>
                {(inputEmailIsSubscriptionMember && !inputEmailAlreadyAdded) 
                  ? <><AddIcon size='1.4em' color='#fff' /><p>Add as Manager</p></>
                  : <><SendIcon size='1.4em'/><p>Send Invite</p></>
                }
              </div>
              <div className={`absolute top-0 left-0 w-full h-full flex items-center justify-center bg-[#00bc7d] rounded-lg overflow-hidden pointer-events-none ${inviteSentConfirmation || userAddedConfirmation || inputEmailAlreadyAdded || inputEmailAlreadyInvited ? 'opacity-100' : 'opacity-0'} transition-all duration-300`}>
                {inviteSentConfirmation && <div className={`flex items-center justify-center gap-2 animate-right-fade`}><CircleCheckIcon size='1.6em' /> Invite Sent</div> }
                {userAddedConfirmation && <div className={`flex items-center justify-center gap-2 animate-right-fade`}><CircleCheckIcon size='1.6em' /> Added as Manager</div> }
                {inputEmailAlreadyAdded && <div className={`flex items-center justify-center gap-2 animate-right-fade`}><CircleCheckIcon size='1.6em' /><p>Already Added</p></div>}
                {inputEmailAlreadyInvited && <div className={`flex items-center justify-center gap-2 animate-right-fade`}><CircleCheckIcon size='1.6em' /><p>Already Invited</p></div>}
              </div>
            </button>
          </div>
          {/* Account Managers */}
          <div className='flex flex-col gap-y-4'>
            <div className=''>
              <div className={'flex font-bold text-[1.4em] text-[#f9a928]'}>Account Managers</div>
              <div>{projectManagerCount} {projectManagerCount === 1 ? 'person is assigned as an account manager' : 'people are assigned as account managers'}.</div>
            </div>
            {/* narrow view members list */}
            <div className='flex flex-col gap-4'>
              {subscriptionManagers.map((member, index) => (
                (member.id in _users &&
                  <div key={index} className='flex flex-col gap-1.5 md:hidden bg-[#222] p-2 rounded-lg'>
                    <div className='flex items-center gap-2'>
                      <div className='font-bold whitespace-nowrap'>Name:</div>
                      <div className='rounded-[5px]'>{_users[member.id].firstName} {_users[member.id].lastName}</div>
                    </div>
                    <div className='flex items-center gap-2'>
                      <div className='font-bold whitespace-nowrap'>Email:</div>
                      <div className='rounded-[5px]'>{_users[member.id].email}</div>
                    </div>
                    <div className='flex justify-end items-center gap-2'>
                      <button 
                        title="Remove Account Manager" 
                        className='flex gap-1.5 justify-center items-center py-1 px-2 rounded-lg bg-[#444] hover:bg-[#555] w-full'
                        onClick={() => {
                          siteboardAction(REMOVE_SUBSCRIPTION_ADMIN, { subscriptionId: ownedSubscription.id, removedMemberId: member.id });
                        }}
                      >
                        <CloseIcon color='#f12035' size='1em' /> Remove
                      </button>
                    </div>
                  </div>
                )
              ))}
              {pendingSubscriptionManagers.map((pendingMember, index) => (
                <div key={index} className='flex flex-col gap-1.5 md:hidden bg-[#222] p-2 rounded-lg'>
                  <div className='flex items-center gap-1.5 whitespace-nowrap'><ClockIcon size='1.4em'/> Pending Invite</div>
                  <div className='flex items-center gap-2'>
                    <div className='font-bold whitespace-nowrap'>Email:</div>
                    <div className='rounded-[5px]'>{pendingMember.email}</div>
                  </div>
                  <button 
                    title="Remove Account Manager" 
                    className='flex gap-1.5 justify-center items-center py-1 px-2 rounded-lg bg-[#444] hover:bg-[#555] w-full'
                    onClick={() => {
                      siteboardAction(REMOVE_PENDING_SUBSCRIPTION_ADMIN, { subscriptionId: ownedSubscription.id, removedMemberEmail: pendingMember.email });
                    }}
                  >
                    <CloseIcon color='#f12035' size='1em' /> Remove
                  </button>
                </div>
              ))}
            </div>
            {/* wide view members list */}
            {projectManagerCount > 0 &&
              <div className={'hidden md:grid grid-cols-[2fr_2fr_4fr_1fr] gap-3'}>
                <div className='font-bold whitespace-nowrap min-w-[100px]'>Last Name</div>
                <div className='font-bold whitespace-nowrap min-w-[100px]'>First Name</div>
                <div className='font-bold whitespace-nowrap min-w-[100px]'>Email</div>
                <div className='font-bold whitespace-nowrap px-3'></div>
                {subscriptionManagers.map((member, index) => (
                  (member.id in _users &&
                    <Fragment key={index}>
                      <div className='py-1.5 rounded-[5px]'>{_users[member.id].lastName}</div>
                      <div className='py-1.5 rounded-[5px]'>{_users[member.id].firstName}</div>
                      <div className='py-1.5 rounded-[5px]'>{_users[member.id].email}</div>
                      <button 
                        title="Remove Account Manager" 
                        className='flex gap-1.5 justify-end items-center py-1 px-2 rounded-lg bg-[#444] hover:bg-[#555]'
                        onClick={() => {
                          siteboardAction(REMOVE_SUBSCRIPTION_ADMIN, { subscriptionId: ownedSubscription.id, removedMemberId: member.id });
                        }}
                      >
                        <CloseIcon color='#f12035' size='1em' /> Remove
                      </button>
                    </Fragment>
                  )
                ))}
                {pendingSubscriptionManagers.map((pendingMember, index) => (
                  <Fragment key={index}>
                    <div className='flex items-center gap-1.5 whitespace-nowrap col-span-2'><ClockIcon size='1.4em'/> Pending Invite</div>
                    <div className='py-1.5 rounded-[5px]'>{pendingMember.email}</div>
                    <button 
                      title="Remove Account Manager" 
                      className='flex gap-1.5 justify-end items-center py-1 px-2 rounded-lg bg-[#444] hover:bg-[#555]'
                      onClick={() => {
                        siteboardAction(REMOVE_PENDING_SUBSCRIPTION_ADMIN, { subscriptionId: ownedSubscription.id, removedMemberEmail: pendingMember.email });
                      }}
                    >
                      <CloseIcon color='#f12035' size='1em' /> Remove
                    </button>
                  </Fragment>
                ))}
              </div>
            }
          </div>
        </div>
      </div>
    </PopupWindow>
  );
};

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, { siteboardAction })(EditSubscriptionManagersDialogue);
